import { ModuleSlug } from '../const/db/fim-modules';

export interface ConstructionResolution {
  /** Whether this node's subtree is entirely under construction. */
  underConstruction: boolean;
  /** Whether this node has at least one module-leaf descendant (or is one). */
  hasModuleLeaf: boolean;
}

/**
 * Single-pass resolver for the "under construction" rule shared by the canvas
 * hub (`Radial`) and the module catalog (`moduleTree`): a module leaf is under
 * construction when the predicate matches its slug, and a container is under
 * construction only when it has at least one module-leaf descendant and every
 * such descendant is under construction (AND-propagation with a
 * has-descendant guard, so empty/structural containers are never marked).
 *
 * Generic over the node shape via accessor callbacks so it can be reused for
 * any tree representation (e.g. `Radial` instances or `StrictNodeModel`
 * objects) without a shared base type or a second traversal per node.
 *
 * @param node - the node to resolve.
 * @param getModuleSlug - returns the node's module slug, or undefined for containers.
 * @param getChildren - returns the node's (already filtered/effective) children.
 * @param isUnderConstruction - predicate matched against module-leaf slugs.
 * @param onResolved - optional callback invoked with each node's result as the
 * traversal unwinds, for callers that need to apply a side effect per node
 * (e.g. `Radial.construction()`) without walking the tree a second time.
 */
export function resolveConstruction<TNode>(
  node: TNode,
  getModuleSlug: (node: TNode) => ModuleSlug | undefined,
  getChildren: (node: TNode) => readonly TNode[],
  isUnderConstruction: (moduleSlug: ModuleSlug) => boolean,
  onResolved?: (node: TNode, result: ConstructionResolution) => void,
): ConstructionResolution {
  const moduleSlug = getModuleSlug(node);
  let result: ConstructionResolution;

  if (moduleSlug) {
    result = {
      underConstruction: isUnderConstruction(moduleSlug),
      hasModuleLeaf: true,
    };
  } else {
    let hasModuleLeaf = false;
    let allUnderConstruction = true;
    for (const child of getChildren(node)) {
      const childResult = resolveConstruction(
        child,
        getModuleSlug,
        getChildren,
        isUnderConstruction,
        onResolved,
      );
      if (childResult.hasModuleLeaf) {
        hasModuleLeaf = true;
        if (!childResult.underConstruction) allUnderConstruction = false;
      }
    }
    result = {
      underConstruction: hasModuleLeaf && allUnderConstruction,
      hasModuleLeaf,
    };
  }

  onResolved?.(node, result);
  return result;
}
